import { Link } from "react-router-dom";
import EmailAddress, { GMAIL_COMPOSE_URL } from "../EmailAddress/EmailAddress";
// Direct per-file imports: dist/flat is CJS with no sideEffects flag, so the
// barrel index defeats tree-shaking and bundled all 34 logos (~9KB) into
// main.js on every page ("Reduce unused JavaScript" in PSI).
import Visa from "react-payment-logos/dist/flat/Visa";
import Mastercard from "react-payment-logos/dist/flat/Mastercard";
import Amex from "react-payment-logos/dist/flat/Amex";
import Discover from "react-payment-logos/dist/flat/Discover";
import SocialShare from "../SocialShare/SocialShare";
import "./Footer.css";

/* Payment-method tiles.
   The first four come from react-payment-logos (their official flat card
   marks). The last four are self-contained brand tiles — the brand colour and
   the tile shape are baked into each SVG asset — so no CSS colour handling is
   needed and they sit in exactly the same grid as the first four. */
const payAsset = (file: string) =>
  `${process.env.PUBLIC_URL}/images/payments/${file}`;

const payMethods = [
  { name: "American Express", icon: <Amex role="img" aria-label="American Express" /> },
  { name: "Mastercard", icon: <Mastercard role="img" aria-label="Mastercard" /> },
  { name: "Visa", icon: <Visa role="img" aria-label="Visa" /> },
  { name: "Discover", icon: <Discover role="img" aria-label="Discover" /> },
  { name: "Zelle", icon: <img src={payAsset("zelle.svg")} alt="Zelle" width={780} height={500} loading="lazy" decoding="async" /> },
  { name: "Venmo", icon: <img src={payAsset("venmo.svg")} alt="Venmo" width={780} height={500} loading="lazy" decoding="async" /> },
  { name: "Cash App", icon: <img src={payAsset("cashapp.svg")} alt="Cash App" width={780} height={500} loading="lazy" decoding="async" /> },
  { name: "Apple Pay", icon: <img src={payAsset("applepay.svg")} alt="Apple Pay" width={780} height={500} loading="lazy" decoding="async" /> },
];

export default function Footer(): JSX.Element {
  return (
    <footer className="footer-section py-5" id="contact">
      <div className="container">
        <div className="row g-4">
          {/* Brand */}
          <div className="col-lg-3 col-md-6">
            <div className="text-center mb-3 d-none d-md-block">
              <Link to="/" title="GS Limo Elite – Home">
                <img
                  src={`${process.env.PUBLIC_URL}/images/logo-footer-160.webp`}
                  srcSet={`${process.env.PUBLIC_URL}/images/logo-footer-160.webp 160w, ${process.env.PUBLIC_URL}/images/logo-footer-v2.webp 240w`}
                  /* The brand block is d-none below 768px, so phones get a 0px
                     layout size here and download the small candidate instead of
                     fetching a 25KB logo nobody can see. */
                  sizes="(min-width: 768px) 140px, 0px"
                  alt="GS Limo Elite — Luxury Transportation NJ, PA & NY"
                  title="GS Limo Elite — Luxury Transportation NJ, PA & NY"
                  width="240"
                  height="160"
                  loading="lazy"
                  style={{ maxWidth: 140, margin: "0 auto" }}
                />
              </Link>
            </div>
            <p className="footer-desc d-none d-md-block">
              Premium transportation for any occasion. Safe, reliable and
              comfortable rides to your destination.
            </p>
            <h3 className="footer-heading mt-3" style={{ fontSize: "0.85rem", letterSpacing: "2px" }}>
              FOLLOW &amp; SHARE
            </h3>
            <div className="footer-socials mt-2 d-flex gap-2 flex-wrap justify-content-center justify-content-md-start">
              <a
                href="https://web.facebook.com/people/GS-LIMO-ELITE/61585471506965/"
                target="_blank"
                rel="noopener noreferrer"
                className="social-btn"
                aria-label="Facebook"
                title="GS Limo Elite on Facebook"
              >
                <i className="fab fa-facebook-f"></i>
              </a>
              <a
                href="https://www.instagram.com/gslimoselite/"
                target="_blank"
                rel="noopener noreferrer"
                className="social-btn"
                aria-label="Instagram"
                title="GS Limo Elite on Instagram"
              >
                <i className="fab fa-instagram"></i>
              </a>
              <a
                href="https://wa.me/19294526031?text=Hi%20GS%20Limo%20Elite%2C%20I%20would%20like%20a%20quote%20for%20a%20ride."
                target="_blank"
                rel="noopener noreferrer"
                className="social-btn"
                aria-label="WhatsApp"
                title="Chat with GS Limo Elite on WhatsApp"
              >
                <i className="fab fa-whatsapp"></i>
              </a>
              <a
                href="https://www.yelp.com/biz/gs-limo-elite-glassboro-2"
                target="_blank"
                rel="noopener noreferrer"
                className="social-btn"
                aria-label="Yelp"
                title="GS Limo Elite on Yelp"
              >
                <i className="fab fa-yelp"></i>
              </a>
              <a
                href="https://www.tiktok.com/@gslimoelite"
                target="_blank"
                rel="noopener noreferrer"
                className="social-btn"
                aria-label="TikTok"
                title="GS Limo Elite on TikTok"
              >
                <i className="fab fa-tiktok"></i>
              </a>
              <SocialShare />
            </div>
          </div>

          {/* Quick Links */}
          <div className="col-lg-3 col-md-6 footer-col-side">
            <div className="px-lg-4">
              <h3 className="footer-heading">QUICK LINKS</h3>
              <ul className="footer-links">
                {(
                  [
                    ["/", "Home"],
                    ["/fleet", "Fleet"],
                    ["/about", "About"],
                    ["/services", "Services"],
                    ["/privacy-policy", "Privacy Policy"],
                    ["/contact", "Contact Us"],
                  ] as [string, string][]
                ).map(([to, label]) => (
                  <li key={to}>
                    <Link to={to} title={`${label} – GS Limo Elite`}>
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contact */}
          <div className="col-lg-3 col-md-6 footer-col-side">
            <h3 className="footer-heading">CONTACT US</h3>
            <ul className="footer-contact">
              <li>
                <i className="fas fa-phone-alt"></i>
                <a
                href="tel:9294526031"
                title="Call GS Limo Elite at (929) 452-6031"
              >
                (929) 452-6031
              </a>
              </li>
              <li>
                <i className="fas fa-envelope"></i>
                <a
                  href={GMAIL_COMPOSE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Email GS Limo Elite"
                >
                  <EmailAddress />
                </a>
              </li>
              <li>
                <i className="fas fa-globe"></i>
                <a
                  href="https://gslimoelite.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Visit GS Limo Elite Website"
                >
                  gslimoelite.com
                </a>
              </li>
              <li>
                <i className="fas fa-map-marker-alt"></i>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=328+Delsea+Dr+N+%232%2C+Glassboro%2C+NJ+08028%2C+United+States"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="View the GS Limo Elite service area on Google Maps"
                >
                  Glassboro,
                  <br />
                  Serving NJ, PA &amp; NY
                </a>
              </li>
            </ul>
          </div>

          {/* Policy */}
          <div className="col-lg-3 col-md-6">
            <h3 className="footer-heading">Policy Notice</h3>
            <p className="policy-main">
              {"GS Limo Elite does not provide or store "}
              <span className="policy-gold">car seats</span>.
            </p>
            <p className="policy-note mt-2">
              Customers must bring and install their own car seats before
              travel.
            </p>
            <div className="footer-payments mt-3">
              <p className="payments-heading">Accepted Payment Cards</p>
              <span className="payments-accent" aria-hidden="true" />
              <div className="payments-grid">
                {payMethods.map(({ name, icon }) => (
                  <div key={name} className="payment-cell" title={name}>
                    <span className="payment-logo">{icon}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="footer-bottom mt-4">
          <p className="footer-legal text-center mb-0">
            <span>{`© ${new Date().getFullYear()} GS Limo Elite`}</span>
            <span className="footer-legal-sep" aria-hidden="true">
              ·
            </span>
            <Link
              to="/privacy-policy"
              title="GS Limo Elite Privacy Policy"
            >
              Privacy Policy
            </Link>
            <span className="footer-legal-sep" aria-hidden="true">
              ·
            </span>
            <Link
              to="/terms-and-conditions"
              title="GS Limo Elite Terms and Conditions"
            >
              Terms and Conditions
            </Link>
            <span className="footer-legal-sep" aria-hidden="true">
              ·
            </span>
            <span>All Rights Reserved.</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
